import React, { useState } from "react";
import styled from "styled-components";
import { useIntl } from "gatsby-plugin-intl";
import { GatsbyImage } from "gatsby-plugin-image";
import { navigate, useStaticQuery, graphql } from "gatsby";
import get from "lodash/get";

import VariantCard from "./variantCard";
import KCButton from "Components/KCButton";
import { getVariantFromCurStyle } from "../../utils";

const StyledWrapper = styled.div`
  color: ${(props) => props.theme.color[props.textColor]};
  background-color: ${(props) => props.theme.color[props.backgroundColor]};
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledTitle = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-top: 1rem;
  color: ${(props) => props.theme.dimGrey};
  flex: 1;
  text-align: center;
`;

const StyledNavWrapper = styled.div`
  display: flex;
  margin: 0.6rem 0;
  text-align: center;
  & > *:not(:first-child) {
    margin-left: 0.4rem;
  }
`;

const StyledProductGrid = styled.div`
  display: flex;
  width: 100%;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const StyledVariantWrapper = styled.div`
  width: calc(50% - 0.5rem);
  margin-left: 1rem;
  &:nth-child(2n + 1) {
    margin-left: 0;
  }
  display: inline-block;
  ${(props) => props.theme.responsive.mobile} {
    width: calc(25% - 0.75rem);

    &:nth-child(2n + 1) {
      margin-left: 1rem;
    }

    &:nth-child(4n + 1) {
      margin-left: 0;
    }
  }
`;

export default function GridProductModal({
  title,
  productLists,
  textColor,
  backgroundColor,
}) {
  const [listIndex, setListIndex] = useState(0);
  const { allShopifyProductVariant, strapi } = useStaticQuery(graphql`
    {
      allShopifyProductVariant {
        edges {
          node {
            price
            presentmentPrices {
              price {
                amount
                currencyCode
              }
            }
            shopifyId
            sku
            selectedOptions {
              name
              value
            }
            title
          }
        }
      }
      strapi {
        spectacleStyles {
          color {
            name
          }
          lens {
            name
          }
          design {
            name
            kcID
          }
          thumbnail {
            url
            imageFile {
              childImageSharp {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          kcID
        }
      }
    }
  `);
  const intl = useIntl();
  const getSpecStyle = (kcID) => {
    return (strapi?.spectacleStyles || []).find((style) => style.kcID === kcID);
  };
  return (
    <StyledWrapper backgroundColor={backgroundColor} textColor={textColor}>
      <StyledTitle>{title?.[intl.locale]}</StyledTitle>
      {productLists.length > 1 && (
        <StyledNavWrapper>
          {productLists.map((style, i) => (
            <KCButton
              key={`grid-productNavButton-${i}`}
              handleClick={() => setListIndex(i)}
              size="small"
              buttonType={i === listIndex ? "primary" : "default"}
              label={get(style, `title[${intl.locale}]`, "")}
            />
          ))}
        </StyledNavWrapper>
      )}
      <StyledProductGrid>
        {productLists?.[listIndex]?.styles.map(({ style }, i) => {
          return (
            <StyledVariantWrapper
              key={`variantCard-${style.kcID}--${listIndex}-${i}`}
            >
              <VariantCard
                style={getSpecStyle(style.kcID)}
                shopifyVariant={getVariantFromCurStyle(
                  style.kcID,
                  getSpecStyle(style.kcID),
                  "M",
                  allShopifyProductVariant
                )}
                textColor={textColor}
              />
            </StyledVariantWrapper>
          );
        })}
      </StyledProductGrid>
    </StyledWrapper>
  );
}

export const query = graphql`
  fragment gridProductModal on STRAPI_ComponentProductGridProduct {
    id
    __typename
    title {
      en
      es
      it
    }
    productLists {
      title {
        en
        es
        it
      }
      styles {
        style {
          kcID
        }
      }
    }
    textColor {
      color
    }
    backgroundColor {
      color
    }
  }
`;
